import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { FileDown, Loader2, ShieldOff, Wallet, AlertTriangle, History, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBrandFont } from '../utils/brandFont';

const API = window.__BACKEND_URL__;
const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '');

const Stat = ({ label, value, tone, testId }) => (
  <div className="rounded-lg border bg-white dark:bg-slate-900 px-4 py-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-xl font-semibold mt-0.5 ${tone || ''}`}>{value}</div>
  </div>
);

const StatementDownload = ({ token, data, brandColor }) => {
  const [preset, setPreset] = useState(data.presets[0]?.key || 'this_month');
  const [range, setRange] = useState({ start: data.presets[0]?.start, end: data.presets[0]?.end });
  const [reports, setReports] = useState(['statement']);
  const pick = (k) => { setPreset(k); const p = data.presets.find((x) => x.key === k); if (p) setRange({ start: p.start, end: p.end }); };
  const url = `${API}/api/portal/account/${token}/statement.pdf?start_date=${range.start}&end_date=${range.end}&reports=${reports.join(',')}`;
  const sel = 'h-9 rounded-md border px-2 text-sm bg-white dark:bg-slate-900';
  return (
    <Card data-testid="account-statement-card">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" style={{ color: brandColor }} /> Download a statement</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs text-slate-500">Period<br />
            <select value={preset} onChange={(e) => pick(e.target.value)} className={sel} data-testid="account-preset">
              {data.presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              <option value="custom">Custom dates</option>
            </select>
          </label>
          <label className="text-xs text-slate-500">From<br /><input type="date" value={range.start} onChange={(e) => { setPreset('custom'); setRange({ ...range, start: e.target.value }); }} className={sel} data-testid="account-range-start" /></label>
          <label className="text-xs text-slate-500">To<br /><input type="date" value={range.end} onChange={(e) => { setPreset('custom'); setRange({ ...range, end: e.target.value }); }} className={sel} data-testid="account-range-end" /></label>
        </div>
        {data.reports.length > 1 && (
          <div className="flex flex-wrap gap-3 text-sm">
            {data.reports.map((r) => (
              <label key={r} className="flex items-center gap-1.5"><input type="checkbox" checked={reports.includes(r)} onChange={() => setReports((s) => (s.includes(r) ? s.filter((x) => x !== r) : [...s, r]))} data-testid={`account-report-${r}`} />{{ statement: 'Statement', invoice_list: 'Invoice list', job_summary: 'Job completion summary' }[r]}</label>
            ))}
          </div>
        )}
        <Button asChild style={{ background: brandColor }} disabled={!reports.length} data-testid="account-download-btn">
          <a href={reports.length ? url : undefined} target="_blank" rel="noreferrer"><FileDown className="w-4 h-4 mr-1.5" /> Download PDF</a>
        </Button>
      </CardContent>
    </Card>
  );
};

export default function CustomerAccountPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/portal/account/${token}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.status === 404 ? 'not_found' : 'network'));
  }, [token]);
  const branding = data?.branding || {};
  const brandColor = branding.brand_color || '#1E88E5';
  const brandFont = useBrandFont(branding.document_branding, branding.company_id);
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="account-portal-error">
        <Card className="max-w-md w-full"><CardContent className="py-10 text-center space-y-2">
          <ShieldOff className="w-10 h-10 mx-auto text-slate-400" />
          <div className="font-medium">{error === 'not_found' ? 'This account link is no longer valid' : 'Could not load your account'}</div>
          <p className="text-sm text-slate-500">{error === 'not_found' ? 'Ask your service provider for a new link.' : 'Please check your connection and try again.'}</p>
        </CardContent></Card>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const logoUrl = branding.logo_path ? `${API}${branding.logo_path}` : null;
  const s = data.summary;
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20" style={{ fontFamily: brandFont }} data-testid="customer-account-portal">
      <div style={{ height: 4, background: brandColor }} />
      <header className="bg-white dark:bg-slate-900 border-b">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center gap-3">
          {logoUrl && <img src={logoUrl} alt="" className="w-12 h-12 rounded-md object-contain border bg-white" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-500 truncate" data-testid="account-company-name">{branding.company_name || 'Your account'}</div>
            <h1 className="text-2xl font-semibold mt-0.5 truncate" data-testid="account-customer-name">{data.customer.name}</h1>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Balance outstanding" value={money(s.outstanding)} tone={s.outstanding > 0 ? 'text-amber-600' : 'text-emerald-600'} testId="account-outstanding" />
          <Stat label="Past due" value={s.overdue} tone={s.overdue ? 'text-red-600' : ''} testId="account-overdue" />
          <Stat label="Billed this year" value={money(s.billed_ytd)} testId="account-billed-ytd" />
          <Stat label="Paid this year" value={money(s.paid_ytd)} testId="account-paid-ytd" />
        </div>
        {data.open_invoices.length > 0 && (
          <Card data-testid="account-open-invoices">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Wallet className="w-4 h-4" style={{ color: brandColor }} /> Open invoices</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500"><tr><th className="text-left px-4 py-2">Invoice</th><th className="text-left px-2 py-2">Date</th><th className="text-left px-2 py-2">Due</th><th className="text-right px-4 py-2">Amount</th></tr></thead>
                <tbody>
                  {data.open_invoices.map((i) => (
                    <tr key={i.number} className="border-t" data-testid={`account-invoice-${i.number}`}>
                      <td className="px-4 py-2 font-mono">{i.number}</td><td className="px-2 py-2">{i.date}</td>
                      <td className="px-2 py-2">{i.due}{i.days_overdue > 0 && <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-red-600"><AlertTriangle className="w-3 h-3" />{i.days_overdue}d overdue</span>}</td>
                      <td className="px-4 py-2 text-right">{money(i.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {branding.remit_to && <p className="text-xs text-slate-500 px-4 py-3 border-t" data-testid="account-remit-to">How to pay: {branding.remit_to}</p>}
            </CardContent>
          </Card>
        )}
        <StatementDownload token={token} data={data} brandColor={brandColor} />
        <Card data-testid="account-history-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" style={{ color: brandColor }} /> Reports sent to you</CardTitle></CardHeader>
          <CardContent>
            {data.history.length === 0 ? <p className="text-sm text-slate-500 py-2" data-testid="account-history-empty">No reports have been sent yet. Use the download above for a statement of any period.</p> : (
              <ul className="divide-y">
                {data.history.map((h) => (
                  <li key={h.id} className="flex items-center gap-3 py-2 text-sm" data-testid={`account-history-${h.id}`}>
                    <span className="capitalize font-medium">{h.kind}</span>
                    <span className="text-slate-500">{h.start} → {h.end}</span>
                    <span className="text-xs text-slate-400 ml-auto">{fmt(h.sent_at)}</span>
                    <a href={`${API}/api/portal/account/${token}/reports/${h.id}.pdf`} target="_blank" rel="noreferrer" className="text-xs font-medium" style={{ color: brandColor }} data-testid={`account-history-download-${h.id}`}>PDF</a>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <footer className="text-center text-xs text-slate-400 pt-6">{branding.company_name ? `${branding.company_name} · ` : ''}Powered by HinesTime · This is your private, secure link.</footer>
      </main>
    </div>
  );
}
